/**
 * Design token injection for shadow-DOM-based widgets.
 *
 * Copies CSS variables, stylesheets, and inline styles into a shadow root so
 * that widgets render with the correct visual tokens regardless of isolation.
 *
 * Two source paths are supported:
 * - **Embed path** — resources stored in `window.__insidedEmbed.headResources`
 *   by the resource-loader (external host pages).
 * - **Full-page path** — resources already present in `document.head` from
 *   server-rendered templates (community platform pages).
 */

/** Whitelisted font CDN hostnames whose stylesheets need document-level @font-face registration. */
const FONT_HOSTS = ['fonts.googleapis.com', 'fonts.gstatic.com']

function isFontStylesheet(href: string): boolean {
  try {
    return FONT_HOSTS.includes(new URL(href).hostname)
  } catch {
    return false
  }
}

/**
 * Injects design tokens (CSS variables, stylesheets, inline styles) into a
 * shadow root. Automatically selects the correct source based on context:
 * embed global resources when available, otherwise document.head.
 */
export function injectDesignTokens(shadowRoot: ShadowRoot): void {
  injectHeadResources(shadowRoot)

  if (!(window as any).__insidedEmbed?.headResources) {
    injectDesignTokensFromDocument(shadowRoot)
  }
}

/**
 * Embed path: the resource-loader stores parsed head resources (stylesheets, styles)
 * in window.__insidedEmbed.headResources. Inject them into the shadow root so the
 * widget renders with the correct fonts, CSS variables and custom styles.
 *
 * External font stylesheets (different base domain than the community) are also
 * added to document.head because @font-face rules loaded exclusively inside
 * shadow DOM are not reliably registered globally across all browsers.
 */
function injectHeadResources(shadowRoot: ShadowRoot): void {
  const res = (window as any).__insidedEmbed?.headResources as
    | { stylesheets?: string[]; styles?: Array<{ id?: string; content: string }> }
    | undefined
  if (!res) return

  for (const href of res.stylesheets ?? []) {
    if (!shadowRoot.querySelector(`link[href="${href}"]`)) {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = href
      shadowRoot.prepend(link)
    }

    if (isFontStylesheet(href) && !document.head.querySelector(`link[href="${href}"]`)) {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = href
      document.head.appendChild(link)
    }
  }

  for (const { id, content } of res.styles ?? []) {
    if (id && shadowRoot.getElementById(id)) continue
    const style = document.createElement('style')
    if (id) style.id = id
    style.textContent = id === 'css-variables' ? content.replace(/html\s*\{/, ':host {') : content
    shadowRoot.prepend(style)
  }
}

/**
 * Full community page fallback: css-variables and font links are in document.head
 * from server-rendered templates. Copy them into the shadow root if not already present.
 */
function injectDesignTokensFromDocument(shadowRoot: ShadowRoot): void {
  if (!shadowRoot.getElementById('css-variables')) {
    const source = document.getElementById('css-variables')
    if (source instanceof HTMLStyleElement && source.textContent) {
      const style = document.createElement('style')
      style.id = 'css-variables'
      style.textContent = source.textContent.replace(/html\s*\{/, ':host {')
      shadowRoot.prepend(style)
    }
  }

  const links = document.head.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')
  for (const link of links) {
    const href = link.getAttribute('href')
    if (!href) continue
    if (shadowRoot.querySelector(`link[href="${href}"]`)) continue
    shadowRoot.prepend(link.cloneNode(true))
  }
}
